import * as _ from "lodash"

import {
    IEntity,
    IEntityCreateOptions,
    IEntityHeader,
    IEntityManager,
    IEntityManagerFindOptions, IEntityManagerQueryOptions
} from "../PA.MD.Core/PA.MD.IF.Entity";
import {PASearch} from "../PA.Core/PASearch";
import {PAdbContext} from "../PA.Core/PAdbContext";
import {EntityTypeUtils} from "./EntityTypeUtils";
import {CreateOptions, ESRecordResolver} from "../PA.Core/lib/ESRecordResolver";
import {
    esWB_Customer,
    esWB_Customer_Calendar,
    esWB_Customer_Distribution_Relationship,
    esWB_Customer_External_Relationship,
    esWB_Customer_Product, esWB_Customer_Proxy_Relationship,
    esWB_Customer_Relationship,
    esWB_Customer_Role,
    esWB_Customer_Role_Relationship,
    esWB_Customer_SalesArea_Relationship,
    esWB_Customer_Tag_Relationship,
    esWB_Customer_Type,
    esWB_DistribChannel,
    esWB_Division, esWB_Entity,
    esWB_SalesArea,
    esWB_SalesOrg
} from "../PA.Core/PAdbSchema";
import {CustomerEntity} from "./CustomerEntity";
import {WB_CustomerRow} from "../PA.Core/PAdbSchemaRows";
import {CustomerBO} from "../PA.MD.BusinessObjects/CustomerBO";
import {ISchema} from "../PA.Core/PA.Core.IF.Entity";
import {PAEntity} from "../PA.Core/PAEntity";


let stringFields = ['ACCOUNT_GROUP', 'CUSTOMER_CLASSIFICATION', 'CUSTOMER_TYPE', 'CUSTOMER_TYPE_NAME',
    'ENTITY_ID', 'ENTITY_NAME', 'EXT_DELETION_IND', 'ID_UI', 'INDUSTRY_CODE1', 'INDUSTRY_CODE2',
    'INDUSTRY_CODE3', 'KEY_SET', 'MANUAL_IND', 'MDSET_ID', 'MDSET_NAME', 'NAME', 'REGIONAL_MARKET',
    'SALESAREA_ID', 'SALESAREA_NAME', 'SALESORG_ID', 'SALESORG_NAME', 'SOURCE_ID', 'VAT_LIABLE_IND', 'ACTIVE_IND']

let numberFields = ['WB_ENTITY_ID', 'WB_ID', 'WB_MDSET_ID', 'WB_SALESAREA_ID', 'WB_SALESORG_ID', 'WB_COMPANY_ID']

const vWB_Customer_Entity_View_Fields: { [id: string]: { type: string } } = PASearch.createSimpleSchema(stringFields, numberFields)


export class CustomerEntityManager
    implements IEntityManager {
    constructor() {
    }

    async findEntitiesByType(wbEntityId: number, arrWbId: number[], options?: IEntityManagerFindOptions): Promise<IEntity[]> {

        let rv: IEntity[] = []
        let dbContext: PAdbContext = options?.dbContext
        if (_.isEmpty(dbContext))
            dbContext = new PAdbContext()
        let et = await EntityTypeUtils.getEntityTypeRow(wbEntityId)
        if (_.isEmpty(et))
            return []

        // Use record resolver to get data, create simple Entity for all Customers
        const customer_options: CreateOptions = {
            standardConditions: {
                [esWB_Customer_Relationship]: {"DELETED_IND": {"notIn": ['1']}},
                [esWB_Customer_Tag_Relationship]: {"DELETED_IND": {"notIn": ['1']}},
                [esWB_Customer_SalesArea_Relationship]: {"DELETED_IND": {"notIn": ['1']}},
                [esWB_Customer_Role_Relationship]: {"DELETED_IND": {"notIn": ['1']}}
            },
            dctIncludeBelongsToNamedRelations: {
                [esWB_Customer_Role]: [esWB_Customer_Type],
                [esWB_SalesArea]: [esWB_SalesOrg, esWB_Division, esWB_DistribChannel]
            },
            dctExcludeHasManyNamedRelations: {
                [esWB_Customer]: [esWB_Customer_Relationship, esWB_Customer_Tag_Relationship,
                    esWB_Customer_Calendar, esWB_Customer_Distribution_Relationship,
                    esWB_Customer_External_Relationship,
                    esWB_Customer_Product, esWB_Customer_Proxy_Relationship]
            }
        }

        let rr = new ESRecordResolver(dbContext, customer_options)
        let rows = <WB_CustomerRow[]>await rr.findAllWithRelationships(esWB_Customer, arrWbId, {})
        // Instantiate Customer Entities with rows
        for (let aRow of rows) {
            let ce = new CustomerEntity(aRow)
            await ce.OnLoadComplete()
            rv.push(ce)
        }

        return rv
    }

    async findEntityByType(wbEntityId: number, wbId: number, options?: IEntityManagerFindOptions): Promise<IEntity> {
        let arrEntities = await this.findEntitiesByType(wbEntityId, [wbId], options)
        if (arrEntities.length)
            return arrEntities[0]
        return undefined
    }

    async findEntityHeader(wbEntityId: number, wbId: number, options?: IEntityManagerFindOptions): Promise<IEntityHeader> {

        let dbContext: PAdbContext = options?.dbContext
        if (_.isEmpty(dbContext))
            dbContext = new PAdbContext()
        let et = await EntityTypeUtils.getEntityTypeRow(wbEntityId)
        if (_.isEmpty(et))
            return
        if (!(wbId > 0))
            return undefined
        let arrRow = <WB_CustomerRow[]>await dbContext.findUsingCache(esWB_Customer, [wbId])

        if (_.isEmpty(arrRow))
            return undefined

        let row = _.first(arrRow)
        return {
            dbContext: dbContext,
            wbMasterDataSetId: row.cif.WB_MDSET_ID,
            mapperName: esWB_Customer,
            wb_id: wbId,
            id_ui: row.cif.CUSTOMER_ID,
            key_set: row.cif.CUSTOMER_SET_ID,
            name: row.cif.CUSTOMER_NAME
        }
    }


    async queryEntitiesByType(wbEntityId: number, condition: { [p: string]: any }, options: IEntityManagerQueryOptions): Promise<number[]> {

        let cond = condition || {}
        if ('where' in condition)
            cond = condition.where || {}

        let pCond: { where: any } = {where: {}}
        if (!_.isEmpty(cond))
            pCond = {where: cond}

        // @ts-ignore
        pCond.where.WB_ENTITY_ID = wbEntityId

        if (options?.bActiveOnly)
            pCond.where.ACTIVE_IND = '1'


        let results = await PASearch.find('vUI_Customer_Entity_Search',
            pCond,
            vWB_Customer_Entity_View_Fields,
            [],
            0,
            ['WB_ID'])

        return _.map(results, (a) => +a.WB_ID)
    }

    async queryEntitiesByTypeEnterpriseType(wbEntityId: number, wbEntityId_Enterprise: number, condition: { [p: string]: any }, options: IEntityManagerQueryOptions): Promise<{ wbId: number; wbId_Ent: number }[]> {
        let cond = condition || {}
        if ('where' in condition)
            cond = condition.where || {}

        let pCond: { where: any } = {where: {}}
        if (!_.isEmpty(cond))
            pCond = {where: cond}

        // @ts-ignore
        pCond.where.WB_ENTITY_ID = wbEntityId

        if (options?.bActiveOnly)
            pCond.where.ACTIVE_IND = '1'

        let sEntWbIdFieldName = ''
        let wbEntityRow_Ent = await EntityTypeUtils.getEntityTypeRow(wbEntityId_Enterprise)
        if (_.isEmpty(wbEntityRow_Ent))
            return []

        switch (wbEntityRow_Ent.cif.ENTITY_ID) {
            case "Company":
                sEntWbIdFieldName = 'WB_COMPANY_ID'
                break
            case "SalesOrg":
                sEntWbIdFieldName = 'WB_SALESORG_ID'
                break
            case "SalesArea":
                sEntWbIdFieldName = 'WB_SALESAREA_ID'
                break
        }

        if (_.isEmpty(sEntWbIdFieldName))
            return []


        let results = await PASearch.find('vUI_Customer_Entity_Search',
            pCond,
            vWB_Customer_Entity_View_Fields,
            [],
            0,
            ['WB_ID', sEntWbIdFieldName])

        return _.map(results, (a) => {
            return {wbId: +a.WB_ID, wbId_Ent: +a[sEntWbIdFieldName]}
        })
    }


    async checkEntityExists(wbEntityId: number, key_set: string, id_ui: string): Promise<{ wbId: number, wbEntityIds: number[], entityIds: string[], dupError?: boolean }> {

        let e = await EntityTypeUtils.getEntityTypeRow(wbEntityId)

        //--
        let results: any[] = await PASearch.find('vUI_Customer_Entity_Search',
            {
                where: {
                    WB_MDSET_ID: e.cif.WB_MDSET_ID,
                    ID_UI: id_ui,
                    KEY_SET: key_set
                }
            },
            vWB_Customer_Entity_View_Fields,
            [],
            0,
            ['WB_ID', 'WB_ENTITY_ID', 'ENTITY_ID'])

        if (results.length) {
            let wbId = +results[0].WB_ID
            let rv: { wbId: number, wbEntityIds: number[], entityIds: string[], dupError?: boolean } = {
                wbId: wbId,
                wbEntityIds: [],
                entityIds: []
            }
            _.forEach(results, (aRow) => {
                let rWbId = +aRow.WB_ID
                let rWbEntityId = +aRow.WB_ENTITY_ID
                let rEntityId = aRow.ENTITY_ID
                rv.wbEntityIds.push(rWbEntityId)
                rv.entityIds.push(rEntityId)

                if (rWbId != rv.wbId)
                    rv.dupError = true
            })
            return rv
        } else
            return {wbId: undefined, wbEntityIds: [], entityIds: []}

    }


    async createEntity(wbEntityId: number, key_set: string, id_ui: string, name: string, options?: IEntityCreateOptions): Promise<IEntity> {
        try {
            let e = await EntityTypeUtils.getEntityTypeRow(wbEntityId)

            let entityCheck = await this.checkEntityExists(wbEntityId, key_set, id_ui)
            if (_.isUndefined(entityCheck))
                throw new Error("Failed Entity Check")

            if (entityCheck?.dupError)
                throw new Error(`Failed Entity Check - duplicate: ${key_set}, id: ${id_ui}`)

            if (entityCheck.wbId > 0)
                throw new Error(`Entity Exists - duplicate, key_set: ${key_set}, id: ${id_ui}.  WbId: ${entityCheck.wbId}`)

            let dbContext: PAdbContext = options?.dbContext
            if (_.isEmpty(dbContext))
                dbContext = new PAdbContext()
            let aCustomerBO = new CustomerBO()
            await aCustomerBO.LoadNew({
                dbContext: dbContext,
                wbMasterDataSetId: e.cif.WB_MDSET_ID,
                sKeySetId: key_set,
                sId: id_ui,
                sName: name,
                wbCustMId: e.cif.META_WB_ID,
                wbSalesAreaId: options.wbSalesAreaId || []
            })
            await aCustomerBO.Save()

            return aCustomerBO.Entity
        } catch (err) {
            throw new Error(err)
        }
    }

    async getQuerySchema(wbEntityId: number): Promise<ISchema> {
        let dbContext: PAdbContext = new PAdbContext()
        let entity = await dbContext.find(esWB_Entity, wbEntityId)
        let entityService: PAEntity = new PAEntity()
        return await entityService.getEntitySchema(0, entity.cif.ENTITY_NAME, {sEntityType:entity.cif.ENTITY_DIMENSION})
    }

    async getUniqueStringValues(wbEntityId: number, propertyName: string): Promise<string[]> {
        return
    }
}